import { ArrowRight, Users } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BrandMark } from "../../components/BrandMark";
import { apiFetch, ApiError } from "../../lib/api";
import { authClient } from "../../lib/auth-client";

interface InvitationInfo {
  id: string;
  email: string;
  role: string;
  status: string;
  expiresAt: string;
  organizationName: string;
  inviterName: string;
}

const roleLabels: Record<string, string> = {
  admin: "Admin",
  editor: "Editor",
  reporter: "Reporter (nur Feldberichte)",
};

type Step = "loading" | "invalid" | "signup" | "ready" | "mismatch";

interface SessionPayload {
  user: { id: string; email: string; emailVerified: boolean };
}

export function InvitationPage() {
  const { invitationId = "" } = useParams();
  const [step, setStep] = useState<Step>("loading");
  const [info, setInfo] = useState<InvitationInfo | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    (async () => {
      const [fetched, session] = await Promise.all([
        apiFetch<InvitationInfo>(`/api/invitations/${invitationId}`).catch(
          () => null,
        ),
        apiFetch<SessionPayload | null>("/api/auth/get-session").catch(
          () => null,
        ),
      ]);
      if (!active) return;
      setInfo(fetched);
      if (
        !fetched ||
        fetched.status !== "pending" ||
        new Date(fetched.expiresAt) < new Date()
      ) {
        setStep("invalid");
        return;
      }
      if (session) {
        setStep(
          session.user.email.toLowerCase() === fetched.email.toLowerCase()
            ? "ready"
            : "mismatch",
        );
      } else {
        setStep("signup");
      }
    })();
    return () => {
      active = false;
    };
  }, [invitationId]);

  const accept = async () => {
    if (!info) return;
    setBusy(true);
    setError("");
    try {
      await apiFetch("/api/auth/organization/accept-invitation", {
        method: "POST",
        body: JSON.stringify({ invitationId: info.id }),
      });
      navigate("/admin", { replace: true });
    } catch (caught) {
      setBusy(false);
      if (caught instanceof ApiError && caught.status === 400) {
        setStep("invalid");
        return;
      }
      setError(
        "Die Einladung konnte nicht angenommen werden. Bitte versuche es erneut.",
      );
    }
  };

  const signUp = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!info) return;
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await apiFetch(`/api/invitations/${info.id}/register`, {
        method: "POST",
        body: JSON.stringify({
          name: String(form.get("name") || "").trim(),
          password: String(form.get("password")),
        }),
      });
      navigate("/admin", { replace: true });
    } catch (caught) {
      setBusy(false);
      if (caught instanceof ApiError && caught.status === 410) {
        setStep("invalid");
        return;
      }
      setError(
        caught instanceof ApiError && caught.message
          ? caught.message
          : "Das Konto konnte nicht erstellt werden. Bitte versuche es erneut.",
      );
    }
  };

  const signOutAndContinue = async () => {
    setError("");
    await authClient.signOut();
    setStep("signup");
  };

  const roleLabel = info ? (roleLabels[info.role] ?? info.role) : "";

  return (
    <div className="auth-simple">
      <BrandMark inverse />
      <main id="main" className="auth-card auth-card--invite">
        <span className="auth-card__icon">
          <Users />
        </span>
        <h1>Zusammen live.</h1>
        {step === "loading" && <p>Einladung wird geprüft…</p>}
        {step === "invalid" && (
          <>
            <p>
              Diese Einladung ist nicht mehr gültig – sie ist abgelaufen oder
              wurde zurückgezogen. Frag {info?.inviterName ?? "das Team"} nach
              einer neuen Einladung.
            </p>
            <Link
              to={`/admin/login?next=${encodeURIComponent(window.location.pathname)}`}
            >
              Zum Login
            </Link>
          </>
        )}
        {info && step !== "loading" && step !== "invalid" && (
          <p>
            <strong>{info.organizationName}</strong> lädt{" "}
            <strong>{info.email}</strong> als {roleLabel} ein – von{" "}
            {info.inviterName}.
          </p>
        )}
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        {step === "ready" && (
          <button onClick={() => void accept()} disabled={busy}>
            {busy ? "Wird angenommen…" : "Einladung annehmen"}{" "}
            <ArrowRight size={17} />
          </button>
        )}
        {step === "signup" && (
          <form onSubmit={signUp}>
            <label>
              <span>Name</span>
              <input name="name" autoComplete="name" required autoFocus />
            </label>
            <label>
              <span>Passwort (mindestens 12 Zeichen)</span>
              <input
                name="password"
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
              />
            </label>
            <button type="submit" disabled={busy}>
              {busy ? "Wird eingerichtet…" : "Los geht’s"}{" "}
              <ArrowRight size={17} />
            </button>
          </form>
        )}
        {step === "mismatch" && (
          <>
            <p className="auth-card__hint">
              Du bist mit einer anderen Adresse angemeldet. Diese Einladung gilt
              für {info?.email}.
            </p>
            <button onClick={() => void signOutAndContinue()} disabled={busy}>
              Abmelden & fortfahren <ArrowRight size={17} />
            </button>
          </>
        )}
        {step === "signup" && (
          <Link
            to={`/admin/login?next=${encodeURIComponent(window.location.pathname)}`}
          >
            Schon ein Konto? Anmelden
          </Link>
        )}
      </main>
    </div>
  );
}
